import { SET_QUERY_STRINGS } from 'constants/dbRecords';
import { ENV_CONFIG } from 'config/environment';
import { useAppSelector } from 'helpers/hooks';
import { trackEvent } from 'helpers/mixpanelEvents';
import {
  formatCurrencyCompact,
  formatNumberAsCurrency,
  getPathWithRef,
  getUrlParams,
} from 'helpers/utils';
import { getRecommendationsData } from 'selectors/recommendations';
import { getProfileConfig } from 'selectors/siteConfig';
import { getCandidatesByRecency } from 'selectors/contributionSummary';
import { getCyclePageQueryStrings, getTransactionsSummary } from 'selectors/transactions';
import { getAllPages } from 'selectors/pages';
import { getUserData } from 'selectors/user';
import { getImpactReportState } from 'selectors/impactReport';
import { getFillOrder, TOTAL_SLOTS } from './fillOrder';
import { CandidateCircles } from './CandidateCircles';
import { Issue } from './Issue';
import { CURRENT_CYCLE_YEAR, FALLBACK_IMPACT_QUERY_STR, getImpactQueryStr } from '../utils';
import classNames from 'classnames';
import { FC, useEffect, useRef, useState } from 'react';
import { ModalComponent } from 'components/Modal';
import { Link as RouterLink } from 'react-router-dom';
import Icon, { IconNames } from 'components/Icon';
import theme from 'config/theme';
import { DonationHistoryTab } from '../DonationHistory';
import { useGateValue } from '@statsig/react-bindings';
import LottieOrbs from 'components/LottieElements/Orbs';
import { SHOW_IMPACT_REPORT_GATE } from 'constants/statsig';

const DEFAULT_RECOMMENDATION_BUDGET = 50;

interface Props {
  isCommunityReportReady: boolean;
  onScrollToHistory: (tab?: DonationHistoryTab) => void;
  showImpactSlides: () => void;
}

export const CycleSection: FC<Props> = ({
  isCommunityReportReady,
  onScrollToHistory,
  showImpactSlides,
}) => {
  const { urlRef } = getUrlParams();
  const refCode = urlRef ? `&ref=${urlRef}-PRD` : '&ref=PRD';
  const showImpactReport = useGateValue(SHOW_IMPACT_REPORT_GATE);

  const {
    userData,
    recommendations,
    summary,
    profileConfig,
    pages,
    completedQueryStrs,
    candidates,
    impactReport,
  } = useAppSelector(state => ({
    userData: getUserData(state),
    recommendations: getRecommendationsData(state),
    summary: getTransactionsSummary(state),
    profileConfig: getProfileConfig(state, 'profile_impact'),
    pages: getAllPages(state),
    completedQueryStrs: getCyclePageQueryStrings(state),
    candidates: getCandidatesByRecency(state),
    impactReport: getImpactReportState(state),
  }));

  const report = impactReport?.data;
  const hasPersonalReport = Boolean(report?.donation_amount_total);
  const isImpactReportPending = !report && !impactReport?.error;
  const hasImpactReportFailed = Boolean(impactReport?.error) && impactReport?.errorStatus !== 404;

  // Shown while pending too, so a user whose report is on its way doesn't see
  // the donate CTA swap out for it.
  const showImpactCard =
    showImpactReport &&
    !hasImpactReportFailed &&
    (isCommunityReportReady || hasPersonalReport || isImpactReportPending);

  const hasTrackedSectionView = useRef(false);
  useEffect(() => {
    if (!showImpactCard || isImpactReportPending || hasTrackedSectionView.current) return;
    hasTrackedSectionView.current = true;
    trackEvent('Profile Impact Report Section Viewed', {
      has_personal_report: hasPersonalReport,
    });
  }, [showImpactCard, isImpactReportPending, hasPersonalReport]);

  const { slots, overflow, completed } = getFillOrder(pages, userData?.issues, completedQueryStrs);

  const [isCompletedModalOpen, setIsCompletedModalOpen] = useState(false);

  const issuesSupported = Math.min(completed.length, TOTAL_SLOTS);

  const getRecommendedDonationAmount = () => {
    if (!userData || !userData.monthly_budget || Number(userData.monthly_budget) === 0) {
      return DEFAULT_RECOMMENDATION_BUDGET;
    }
    return Number(userData.monthly_budget);
  };

  const handleClick = () => {
    trackEvent('Profile Donate Button Clicked', {
      user_id: userData?.id,
      tag_id:
        recommendations?.length > 0
          ? (getImpactQueryStr(recommendations, profileConfig?.profile_impact_query_str) ?? '')
          : '',
      recommended_amount: userData?.monthly_budget || 0,
    });
  };

  // Two groups so the overflow tiles can sit between them.
  const renderIssues = (issueSlots: typeof slots) =>
    issueSlots.map(({ page, isComplete, visibleOn }) => (
      <Issue
        key={page.query_str}
        className={classNames(isComplete ? 'complete-issue' : 'incomplete-issue', {
          'hide-mobile': !visibleOn.mobile,
          'hide-desktop': !visibleOn.desktop,
        })}
        isComplete={isComplete}
        page={page}
        amount={getRecommendedDonationAmount()}
        refCode={refCode}
      />
    ));

  const onViewImpactSlides = () => {
    showImpactSlides();
    trackEvent('Profile View Personal Impact Report Button Clicked', null);
  };

  return (
    <div className="cycle-section">
      <div className="cycle-section-left">
        <button type="button" className="cycle-metric" onClick={() => onScrollToHistory()}>
          <Icon
            className="cycle-metric-chevron"
            name={IconNames.ChevronDown}
            color={theme.colors.inkBlue}
            size={20}
          />
          <div className="h2-dtm cycle-metric-value">
            {formatCurrencyCompact(summary?.total_amount_donated_ytd || 0)}
          </div>
          <div className="body-small-text color-black shadow-5">Donated this cycle</div>
        </button>
        <button
          type="button"
          className="cycle-metric"
          onClick={() => onScrollToHistory('thisCycle')}
        >
          <Icon
            className="cycle-metric-chevron"
            name={IconNames.ChevronDown}
            color={theme.colors.inkBlue}
            size={20}
          />
          <div className="cycle-metric-row">
            <div className="h2-dtm cycle-metric-value">{summary?.total_candidates_ytd ?? '-'}</div>
            <CandidateCircles candidates={candidates} />
          </div>
          <div className="body-small-text color-black shadow-5">Candidates supported</div>
        </button>
        <div className="cycle-issues">
          <div className="cycle-issues-header">
            <div className="cycle-issues-title">Top issues supported</div>
            <div className="cycle-issues-counter">
              <span className="cycle-issues-counter-value">{issuesSupported}</span> of {TOTAL_SLOTS}
            </div>
          </div>
          <div className="cycle-issue-items">
            {renderIssues(slots.filter(slot => slot.isComplete))}
            {/* One pill per breakpoint - the counts differ, and CSS picks which shows. */}
            {overflow.desktop > 0 && (
              <button
                type="button"
                className="cycle-issue cycle-issue-overflow hide-mobile"
                onClick={() => setIsCompletedModalOpen(true)}
              >
                {`+${overflow.desktop} more`}
              </button>
            )}
            {overflow.mobile > 0 && (
              <button
                type="button"
                className="cycle-issue cycle-issue-overflow hide-desktop"
                onClick={() => setIsCompletedModalOpen(true)}
              >
                {`+${overflow.mobile} more`}
              </button>
            )}
            {renderIssues(slots.filter(slot => !slot.isComplete))}
          </div>
        </div>
        <div className="cycle-issues-edit">
          <RouterLink className="cycle-issues-edit-link" to={getPathWithRef('/profile/edit')}>
            Edit my top issues
          </RouterLink>
        </div>
      </div>
      <ModalComponent
        isOpen={isCompletedModalOpen}
        closeModal={() => setIsCompletedModalOpen(false)}
        contentLabel="Completed top issues"
        style={{ width: '760px' }}
      >
        <div className="completed-issues-modal-header">
          <div className="completed-issues-modal-title">Issues you've supported this cycle</div>
          <div className="completed-issues-modal-subtitle">
            {`${completed.length} issue${completed.length !== 1 ? 's' : ''} completed`}
          </div>
        </div>
        <div className="completed-issue-list">
          {completed.map(page => (
            <Issue
              key={page.query_str}
              className="modal-issue"
              isComplete
              page={page}
              amount={getRecommendedDonationAmount()}
              refCode={refCode}
            />
          ))}
        </div>
      </ModalComponent>
      <div
        className={classNames('cycle-section-right', {
          p0: showImpactCard,
        })}
      >
        {showImpactCard ? (
          <LottieOrbs>
            <div className="display-flex flex-direction-column">
              {isImpactReportPending ? null : (
                <>
                  <div className="eyebrow-text color-white mb10">NEW!</div>
                  {hasPersonalReport ? (
                    <>
                      <div className="h4-text color-white">{`Your ${CURRENT_CYCLE_YEAR} Oath Impact Report`}</div>
                      <div className="body-small-text color-white mb20">
                        Your donations made a huge impact in this year’s election. Check it out!
                      </div>
                      <div className="cycle-impact-report-ctas-container">
                        <button
                          className="button-brand button-brand-secondary mb8"
                          onClick={onViewImpactSlides}
                          type="button"
                        >
                          View Now
                        </button>
                        {isCommunityReportReady && (
                          <RouterLink
                            className="impact-report-community-link"
                            to={getPathWithRef('/impact_report')}
                          >
                            View Oath’s Community Report
                          </RouterLink>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="h4-text color-white">{`Oath’s ${CURRENT_CYCLE_YEAR} Community Impact Report`}</div>
                      <div className="body-small-text color-white mb20">
                        See what the Oath community accomplished in this year’s election.
                      </div>
                      <div className="cycle-impact-report-ctas-container">
                        <RouterLink
                          className="button-brand button-brand-secondary"
                          to={getPathWithRef('/impact_report')}
                        >
                          View Community Report
                        </RouterLink>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </LottieOrbs>
        ) : (
          <>
            <div className="eyebrow-text color-electricBlue">
              {profileConfig?.profile_impact_label}
            </div>
            <div className="h3-mobile color-white cycle-impact-heading">{`${formatNumberAsCurrency(getRecommendedDonationAmount())} ${profileConfig?.profile_impact_amount_to}`}</div>
            <div className="body-small-text color-white">{profileConfig?.profile_impact_desc}</div>
            <a
              className="button-brand button-brand-secondary cycle-donate-button"
              href={getPathWithRef(
                `${ENV_CONFIG().SITE_URL}/donate?p=${
                  getImpactQueryStr(recommendations, profileConfig?.profile_impact_query_str) ??
                  FALLBACK_IMPACT_QUERY_STR
                }&amount=${getRecommendedDonationAmount()}${refCode}`
              )}
              target="_blank"
              rel="noreferrer"
              onClick={handleClick}
            >
              {profileConfig?.profile_impact_button}
            </a>
            <RouterLink
              className="cycle-recommendations-link"
              to={getPathWithRef(`/set?p=${SET_QUERY_STRINGS.TOP_ISSUES}`)}
            >
              See more recommendations
            </RouterLink>
          </>
        )}
      </div>
    </div>
  );
};
